import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { trackEvent } from '@/utils/analytics'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { useAppStore } from '../../store'
import { Loader2 } from 'lucide-react'
import type { OnboardingQuestion, OnboardingQuestionType } from '@/service/api'
import { useTranslation } from '@/i18n'

const NEXT_STEP_URL = '/intelligent-board'

// Answer state type
type AnswerValue = string | number | string[]

interface DynamicQuestionsProps {
  onComplete?: () => void
}

const DynamicQuestions = ({ onComplete }: DynamicQuestionsProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const {
    onboardingQuestions,
    isOnboardingQuestionsLoading,
    fetchOnboardingQuestions,
    submitAnswers,
    myOnboardingAnswers,
    fetchMyAnswers,
  } = useAppStore()

  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Fetch questions and existing answers on mount
  useEffect(() => {
    fetchOnboardingQuestions()
    fetchMyAnswers()
    trackEvent('onboarding-step_viewed', { step_name: 'dynamic_questions', step_index: 1 })
  }, [fetchOnboardingQuestions, fetchMyAnswers])

  // Pre-populate answers if user has already answered
  useEffect(() => {
    if (myOnboardingAnswers.length > 0) {
      const existingAnswers: Record<string, AnswerValue> = {}
      myOnboardingAnswers.forEach((answer) => {
        existingAnswers[answer.question_id] = answer.answer_value
      })
      setAnswers(existingAnswers)
    }
  }, [myOnboardingAnswers])

  const handleAnswerChange = (questionId: string, value: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
    // Clear error when user starts typing
    if (errors[questionId]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[questionId]
        return newErrors
      })
    }
  }

  const handleMultiSelectChange = (questionId: string, option: string, checked: boolean) => {
    setAnswers((prev) => {
      const currentValues = (prev[questionId] as string[]) || []
      if (checked) {
        return { ...prev, [questionId]: [...currentValues, option] }
      } else {
        return { ...prev, [questionId]: currentValues.filter((v) => v !== option) }
      }
    })
  }

  const validateAnswers = (): boolean => {
    const newErrors: Record<string, string> = {}

    onboardingQuestions.forEach((question) => {
      if (question.is_required) {
        const answer = answers[question.id]
        if (
          answer === undefined ||
          answer === '' ||
          (Array.isArray(answer) && answer.length === 0)
        ) {
          newErrors[question.id] = t('onboarding:questions.required', {
            defaultValue: 'This field is required',
          })
        }
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateAnswers()) return

    setIsSubmitting(true)
    try {
      // Filter out empty answers
      const answersToSubmit = Object.entries(answers)
        .filter(([_, value]) => {
          if (Array.isArray(value)) return value.length > 0
          return value !== '' && value !== undefined
        })
        .map(([questionId, answerValue]) => ({
          questionId,
          answerValue,
        }))

      if (answersToSubmit.length > 0) {
        await submitAnswers({ answers: answersToSubmit })
      }

      trackEvent('onboarding-questions_completed', {
        question_count: onboardingQuestions.length,
        answers_provided: answersToSubmit.length,
      })

      trackEvent('onboarding-completed', {
        steps_completed: 2,
        steps_skipped: 0,
      })

      if (onComplete) {
        onComplete()
      } else {
        navigate(NEXT_STEP_URL)
      }
    } catch (error) {
      console.error('Failed to submit answers:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSkip = () => {
    // Allow skipping if no required questions
    const hasRequiredQuestions = onboardingQuestions.some((q) => q.is_required)
    if (!hasRequiredQuestions) {
      trackEvent('onboarding-skipped', { step_name: 'questions' })
      trackEvent('onboarding-completed', {
        steps_completed: 1,
        steps_skipped: 1,
      })
      if (onComplete) {
        onComplete()
      } else {
        navigate(NEXT_STEP_URL)
      }
    }
  }

  const renderQuestionInput = (question: OnboardingQuestion) => {
    const { id, question_type, options } = question
    const value = answers[id]
    const error = errors[id]

    switch (question_type as OnboardingQuestionType) {
      case 'text':
        return (
          <Input
            type="text"
            value={(value as string) || ''}
            placeholder={t('onboarding:questions.text_placeholder', {
              defaultValue: 'Enter your answer',
            })}
            onChange={(e) => handleAnswerChange(id, e.target.value)}
            className={error ? 'border-red-500' : ''}
          />
        )

      case 'number':
        return (
          <Input
            type="number"
            value={value !== undefined ? String(value) : ''}
            placeholder={t('onboarding:questions.number_placeholder', {
              defaultValue: 'Enter a number',
            })}
            onChange={(e) => {
              const numValue = e.target.value === '' ? '' : Number(e.target.value)
              handleAnswerChange(id, numValue)
            }}
            className={error ? 'border-red-500' : ''}
          />
        )

      case 'single_select':
        return (
          <Select
            value={(value as string) || ''}
            onValueChange={(val) => handleAnswerChange(id, val)}
          >
            <SelectTrigger className={error ? 'border-red-500' : ''}>
              <SelectValue
                placeholder={t('onboarding:questions.select_placeholder', {
                  defaultValue: 'Select an option',
                })}
              />
            </SelectTrigger>
            <SelectContent>
              {options?.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'multi_select':
        return (
          <div className={`space-y-2 p-3 border rounded-md ${error ? 'border-red-500' : 'border-gray-200 dark:border-dark-border'}`}>
            {options?.map((option) => {
              const currentValues = (value as string[]) || []
              return (
                <div key={option} className="flex items-center gap-2">
                  <Checkbox
                    id={`${id}-${option}`}
                    checked={currentValues.includes(option)}
                    onCheckedChange={(checked) =>
                      handleMultiSelectChange(id, option, !!checked)
                    }
                  />
                  <Label htmlFor={`${id}-${option}`} className="cursor-pointer font-normal">
                    {option}
                  </Label>
                </div>
              )
            })}
          </div>
        )

      default:
        return null
    }
  }

  // Show loading state
  if (isOnboardingQuestionsLoading) {
    return (
      <div className="w-[60%] h-full flex justify-center items-center flex-col gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-gray-500 dark:text-dark-text-muted">
          {t('onboarding:questions.loading', { defaultValue: 'Loading questions...' })}
        </p>
      </div>
    )
  }

  // No questions to show - redirect
  if (onboardingQuestions.length === 0) {
    return (
      <div className="w-[60%] h-full flex justify-center flex-col gap-6">
        <div className="text-center">
          <h2 className="font-bold text-2xl text-gray-600 dark:text-gray-400 mb-4">
            {t('onboarding:done.title', { defaultValue: "You're all set!" })}
          </h2>
          <p className="text-gray-500 dark:text-dark-text-muted mb-6">
            {t('onboarding:done.description', {
              defaultValue: 'No additional questions required at this time.',
            })}
          </p>
          <Button onClick={() => navigate(NEXT_STEP_URL)}>
            {t('onboarding:done.continue', { defaultValue: 'Continue' })}
          </Button>
        </div>
      </div>
    )
  }

  const hasRequiredQuestions = onboardingQuestions.some((q) => q.is_required)

  return (
    <div className="w-[60%] h-full flex justify-center flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="font-bold text-2xl text-gray-600 dark:text-gray-400">
          {t('onboarding:questions.title', { defaultValue: 'Tell us a bit more about yourself' })}
        </h2>
        <p className="text-gray-500 dark:text-dark-text-muted">
          {t('onboarding:questions.description', {
            defaultValue: 'Help us personalize your experience by answering a few questions.',
          })}
        </p>
      </div>

      <form className="flex bg-white dark:bg-dark-card flex-col gap-5" onSubmit={handleSubmit}>
        {onboardingQuestions
          .sort((a, b) => a.display_order - b.display_order)
          .map((question) => (
            <div key={question.id} className="flex flex-col gap-2">
              <Label className="flex items-center gap-1">
                {question.question_text}
                {question.is_required && <span className="text-red-500">*</span>}
              </Label>
              {renderQuestionInput(question)}
              {errors[question.id] && (
                <span className="text-red-500 text-sm">{errors[question.id]}</span>
              )}
            </div>
          ))}

        <hr />

        <footer className="flex justify-between py-3">
          {!hasRequiredQuestions && (
            <Button
              type="button"
              variant="ghost"
              onClick={handleSkip}
              disabled={isSubmitting}
            >
              {t('onboarding:questions.skip', { defaultValue: 'Skip for now' })}
            </Button>
          )}
          <div className="flex-1" />
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('onboarding:questions.submitting', { defaultValue: 'Submitting...' })}
              </>
            ) : (
              t('onboarding:questions.continue', { defaultValue: 'Continue' })
            )}
          </Button>
        </footer>
      </form>
    </div>
  )
}

export default DynamicQuestions
